import React, { useCallback, useContext, useEffect, useState } from 'react';
import { makeSafeRequest } from '../../../common/makeSafeRequest';
import Preloader from '../../../components/common/Preloader';
import { AppInfoContext } from '../../../components/contexts/AppInfoContext';
import SystemPage from '../../../components/system-page/SystemPage';
import { ListForcePackageData, Optional } from '../../../types/common';
import { EffectorData, FiltersData, InputData } from '../../../types/systems';
import { defaultSettings, effectorsSettingsTemplate } from '../effectors/defaultSettings';

const Effectors: React.FC = () => {
	const [filters, setFilters] = useState<FiltersData>();
	const [inputData, setInputData] = useState<InputData>();
	const { currentPid } = useContext(AppInfoContext);

	const adjustSettings = (settings: Optional<EffectorData, 'id'>) => {
		const newSettings = { ...settings };
		if (newSettings.indirect_fire !== undefined)
			newSettings.indirect_fire = newSettings.fire_type === 'INDIRECT' ? true : false;

		return newSettings;
	};

	const initialize = useCallback(
		async (pid: number, fp: number | string, side: number | string) => {
			const filters = await downloadFilters(pid, fp, side);
			const inpData = await downloadInputData();

			if (!filters || !inpData) {
				return false;
			}

			setFilters(filters);
			setInputData(inpData);
			return true;
		},
		[]
	);

	const downloadFilters = async (pid: number, fp: number | string, side: number | string) => {
		return await makeSafeRequest<FiltersData>('effectors/', 'GET', {
			params: { pid: pid, fp: fp, side: side },
		});
	};

	const downloadSystemSettings = async (systemId: number) => {
		return await makeSafeRequest<EffectorData>(`effectors/${systemId}/`, 'GET');
	};

	const downloadInputData = async () => {
		return await makeSafeRequest<InputData>('effectors/input-data/', 'GET');
	};

	const addEffector = async (settings: Optional<EffectorData, 'id'>) => {
		const adjustedSettings: Optional<EffectorData, 'id'> = adjustSettings(settings);
		return await makeSafeRequest<EffectorData>('effectors/', 'POST', {
			payload: adjustedSettings,
			onSuccessMessage: 'Effector added successfully!',
		});
	};

	const deleteEffector = async (settingsId: number) => {
		return await makeSafeRequest<null>(`effectors/${settingsId}/`, 'DELETE', {
			onSuccessMessage: 'Effector deleted successfully!',
		});
	};

	const saveEffector = async (settings: Optional<EffectorData, 'id'>) => {
		const adjustedSettings: Optional<EffectorData, 'id'> = adjustSettings(settings);
		return await makeSafeRequest<EffectorData>(`effectors/${adjustedSettings.id}/`, 'PUT', {
			payload: adjustedSettings,
			onSuccessMessage: 'Effector saved successfully!',
		});
	};

	const duplicateEffector = async (settingsId: number) => {
		return await makeSafeRequest<EffectorData>(`effectors/${settingsId}/duplicate/`, 'POST', {
			onSuccessMessage: 'Effector duplicated successfully!',
		});
	};

	useEffect(() => {
		initialize(currentPid, '', '');
	}, [initialize, currentPid]);

	return filters && inputData ? (
		<SystemPage
			name="effector"
			forcePackageData={filters.fps as ListForcePackageData[]}
			systemsData={filters.effectors as EffectorData[]}
			inputData={inputData}
			initializeFilters={initialize}
			downloadSystemSettings={downloadSystemSettings}
			systemSettingsTemplate={effectorsSettingsTemplate}
			defaultSettings={defaultSettings}
			addSystem={addEffector}
			saveSystem={saveEffector}
			deleteSystem={deleteEffector}
			duplicateSystem={duplicateEffector}
		></SystemPage>
	) : (
		<Preloader></Preloader>
	);
};

export default Effectors;
